import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchCar } from "../api";
import { useI18n, formatPrice } from "../i18n";
import { ArrowLeft, ArrowRight, Cog, Users, Fuel, Calendar, DoorOpen, Check } from "lucide-react";
import Panorama360 from "../components/Panorama360";

const CarDetail = () => {
  const { carId } = useParams();
  const { lang, t } = useI18n();
  const [car, setCar] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCar(carId).then(setCar).catch((e) => setError(e?.message || "Error"));
  }, [carId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-zinc-400" data-testid="car-not-found">
        <p>Car not found.</p>
        <Link to="/fleet" className="btn-outline mt-6 inline-flex">
          {t.common.backToFleet}
        </Link>
      </div>
    );
  }

  if (!car) {
    return <div className="max-w-7xl mx-auto px-6 py-32" data-testid="car-loading">…</div>;
  }

  const desc = lang === "pt" ? car.description_pt : car.description_en;

  const specs = [
    { icon: <Cog size={18} />, label: t.car.transmission, value: car.transmission },
    { icon: <Fuel size={18} />, label: t.car.fuel, value: car.fuel },
    { icon: <Users size={18} />, label: t.car.seats, value: car.seats },
    { icon: <DoorOpen size={18} />, label: t.car.doors, value: car.doors },
    { icon: <Calendar size={18} />, label: t.car.year, value: car.year },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="car-detail-page">
      <Link
        to="/fleet"
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-mono-ui tracking-widest uppercase"
        data-testid="back-to-fleet"
      >
        <ArrowLeft size={16} /> {t.common.backToFleet}
      </Link>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 relative grain border border-white/10 bg-[#0a0a0a] aspect-[4/3] overflow-hidden">
          <img src={car.image} alt={car.name} className="w-full h-full object-cover" data-testid="car-hero-image" />
          <div className="absolute top-6 left-6 text-label">{car.category}</div>
          <div className="absolute top-6 right-6 bg-[#FFB400] text-white text-xs font-mono-ui px-3 py-1.5 tracking-widest uppercase">
            {car.year}
          </div>
        </div>

        <div className="lg:col-span-5">
          <p className="text-label">{car.brand}</p>
          <h1 className="mt-3 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white" data-testid="car-name">
            {car.name}
          </h1>
          <p className="mt-6 text-zinc-300 leading-relaxed" data-testid="car-description">
            {desc}
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {specs.map((s) => (
              <div key={s.label} className="border border-white/10 p-4">
                <div className="flex items-center gap-2 text-zinc-500">{s.icon}<span className="text-label">{s.label}</span></div>
                <p className="mt-2 text-white font-heading text-lg">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-baseline justify-between border-t border-white/10 pt-6">
            <div>
              <p className="text-label">{t.common.from}</p>
              <p className="font-heading text-5xl font-bold text-white">
                {formatPrice(car.price_per_day)}
                <span className="text-sm text-zinc-500 font-mono-ui ml-2">
                  {t.common.perDay}
                </span>
              </p>
            </div>
          </div>

          <Link to={`/booking/${car.id}`} className="mt-8 btn-primary w-full justify-center glow-coral" data-testid="cta-book-this">
            {t.car.bookThis} <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      <div className="mt-20 border-t border-white/10 pt-14" data-testid="car-features-section">
        <p className="text-label text-[#FFB400]"><span className="line-accent" />{t.car.includes}</p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {car.features.map((f) => (
            <div key={f} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/10 p-5">
              <Check size={18} className="text-[#FFB400]" />
              <span className="text-white">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 border-t border-white/10 pt-14" data-testid="car-panorama-section">
        <p className="text-label text-[#FFB400]">
          <span className="line-accent" />
          {lang === "pt" ? "Interior 360°" : "360° Interior"}
        </p>
        <h2 className="mt-3 font-heading text-3xl font-semibold text-white tracking-tight">
          {lang === "pt"
            ? "Conheça o interior antes de reservar."
            : "Step inside before you book."}
        </h2>
        <div className="mt-8">
          <Panorama360
            panoramaUrl={car.panorama_url}
            label={lang === "pt" ? "Interior 360°" : "Interior 360°"}
          />
        </div>
      </div>
    </div>
  );
};

export default CarDetail;
